import React, { useState, useMemo, useEffect } from 'react';
import { useTimeClock } from '../hooks/useTimeClock';
import { convertTimeLogsToEditableSegments, formatDecimalHoursToHHMM, correctDateForTimezone } from '../utils.js';
import { getTodaysShiftForEmployee } from '../../shared/utils.js';
import { EditableTimeSegment, LeaveEntry, FilterSettings, TimeDisplayFormat, EmployeeStatus, TimeLog, TimeEntrySource, Activity, EmployeeRole, ActivityCalculation, LeaveCategoryCalculation } from '../../shared/types.js';
import Button from './Button';
import EntryEditorModal from './EntryEditorModal';
import SelectEntriesModal from './SelectEntriesModal';
import InsertLunchModal from './InsertLunchModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import AddLeaveModal from './AddLeaveModal';
import LeaveEditorModal from './LeaveEditorModal';
import { PlusSquareIcon, EditIcon, TrashIcon, FilterIcon, BurgerIcon, CalendarPlusIcon, LockIcon, RefreshCwIcon, CalendarDaysIcon } from './icons';
import ContextMenu, { ContextMenuItem } from './ContextMenu';
import LeaveBalancesModal from './LeaveBalancesModal';

interface ToolbarButtonProps {
    icon: React.ReactNode;
    label: string;
    onClick?: () => void;
    disabled?: boolean;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ icon, label, onClick, disabled }) => {
    const classes = `flex flex-col items-center justify-center text-center p-4 bg-gray-200 dark:bg-gray-800 rounded-lg shadow-md transition-all duration-200 ease-in-out
    ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-300 dark:hover:bg-gray-700 hover:scale-105 active:scale-100'}`;
    return (
        <button onClick={onClick} disabled={disabled} className={classes} title={disabled ? `${label}` : label}>
            <div className="w-10 h-10 text-brand-secondary">{icon}</div>
            <span className="mt-2 text-sm text-gray-700 dark:text-gray-300 font-medium">{label}</span>
        </button>
    );
};

interface ToolbarSectionProps {
    children: React.ReactNode;
}

const ToolbarSection: React.FC<ToolbarSectionProps> = ({ children }) => (
    <div className="bg-white dark:bg-gray-800/50 rounded-xl p-3 md:p-6 mb-4 md:mb-6 shadow-lg">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-3 md:gap-4">
            {children}
        </div>
    </div>
);

type SortableKeys = 'employeeName' | 'startDate' | 'startTime' | 'stopDate' | 'stopTime' | 'activity' | 'hours' | 'default';

const EntryEditor: React.FC = () => {
    const { employees, timeLogs, activities, leaveEntries, leaveCategories, deleteTimeLogs, updateTimeLog, addTimeLog, insertLunchBreak, updateEmployee, getGroupForEmployee, shifts, schedules, getActivityId, payrollSettings, addLeaveEntry, deleteLeaveEntry, updateLeaveEntry, displayGroups, fetchData } = useTimeClock();
    const employeesInAnyGroup = useMemo(() => {
        const groupEmployeeIds = new Set(displayGroups.flatMap(g => g.employeeIds));
        return employees.filter(e => groupEmployeeIds.has(e.id));
    }, [employees, displayGroups]);
    const activeEmployeesInAnyGroup = useMemo(() => employeesInAnyGroup.filter(e => e.role !== EmployeeRole.INACTIVE), [employeesInAnyGroup]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [lastClickedIndex, setLastClickedIndex] = useState<number | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [isInsertLunchModalOpen, setIsInsertLunchModalOpen] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [isAddLeaveModalOpen, setIsAddLeaveModalOpen] = useState(false);
    const [isLeaveBalancesModalOpen, setIsLeaveBalancesModalOpen] = useState(false);
    const [editingSegment, setEditingSegment] = useState<EditableTimeSegment | null>(null);
    const employeesForEntryModal = useMemo(() => {
        if (editingSegment) {
            const editingEmployee = employeesInAnyGroup.find(e => e.id === editingSegment.employeeId);
            if (editingEmployee && !activeEmployeesInAnyGroup.some(e => e.id === editingEmployee.id)) {
                return [...activeEmployeesInAnyGroup, editingEmployee];
            }
        }
        return activeEmployeesInAnyGroup;
    }, [editingSegment, employeesInAnyGroup, activeEmployeesInAnyGroup]);
    const [segmentToSplit, setSegmentToSplit] = useState<EditableTimeSegment | null>(null);
    const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
    const [filters, setFilters] = useState<FilterSettings>({
        employeeIds: activeEmployeesInAnyGroup.map(e => e.id),
        startDate: '2000-01-01',
        endDate: '2100-01-01',
        timeDisplayFormat: TimeDisplayFormat.DECIMAL
    });
    const [sortConfig, setSortConfig] = useState<{ key: SortableKeys, direction: 'asc' | 'desc' }>({ key: 'default', direction: 'asc' });
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; } | null>(null);
    const lockTimestamp = useMemo(() => {
        if (!payrollSettings.startDate) return 0;
        const lockDate = correctDateForTimezone(payrollSettings.startDate);
        lockDate.setHours(0, 0, 0, 0);
        return lockDate.getTime();
    }, [payrollSettings.startDate]);
    const isSegmentLocked = (segment: EditableTimeSegment) => segment.startTime < lockTimestamp;
    const timeLogById = useMemo(() => new Map(timeLogs.map(log => [log.id, log])), [timeLogs]);
    const isAutoClockOutAtShiftStop = (segment: EditableTimeSegment) => {
        if (segment.source !== 'time' || !segment.timeLogId || !segment.endTime || !payrollSettings) return false;
        const log = timeLogById.get(segment.timeLogId);
        if (log?.source !== TimeEntrySource.AUTO) return false;
        const shift = getTodaysShiftForEmployee(segment.employeeId, schedules, shifts, payrollSettings, displayGroups, new Date(segment.startTime));
        if (!shift) return false;
        const [startHours] = shift.startTime.split(':').map(Number);
        const [stopHours, stopMinutes] = shift.stopTime.split(':').map(Number);
        const expectedStop = new Date(segment.startTime);
        if (stopHours < startHours) {
            expectedStop.setDate(expectedStop.getDate() + 1);
        }
        expectedStop.setHours(stopHours, stopMinutes, 0, 0);
        return Math.abs(segment.endTime - expectedStop.getTime()) < 60 * 1000;
    };
    const segments = useMemo(() => {
        if (!hasAppliedFilter) return [];
        const start = correctDateForTimezone(filters.startDate);
        start.setHours(0, 0, 0, 0);
        const startTime = start.getTime();
        const end = correctDateForTimezone(filters.endDate);
        end.setHours(23, 59, 59, 999);
        const endTime = end.getTime();
        const selectedEmployeeIds = new Set(filters.employeeIds);
        const relevantEmployees = employeesInAnyGroup.filter(e => selectedEmployeeIds.has(e.id));
        const relevantEmpNumbers = new Set(relevantEmployees.map(e => e.emp_number));
        const filteredTimeLogs = timeLogs.filter(log => 
            relevantEmpNumbers.has(log.employeeId) &&
            log.startTime <= endTime && 
            (log.endTime || Date.now()) >= startTime
        );
        const filteredLeaveEntries = leaveEntries.filter(entry => 
            entry.employeeIds.some(id => selectedEmployeeIds.has(id)) &&
            entry.startDate <= endTime &&
            entry.endDate >= startTime
        );
        const allSegments = convertTimeLogsToEditableSegments(filteredTimeLogs, filteredLeaveEntries, activities, leaveCategories, relevantEmployees, startTime, endTime);
        return allSegments;
    }, [timeLogs, leaveEntries, activities, leaveCategories, employeesInAnyGroup, filters, hasAppliedFilter]);
    const sortedSegments = useMemo(() => {
        const sortableItems = [...segments];
        const { key, direction } = sortConfig;
        if (key === 'default') {
            return sortableItems.sort((a, b) => a.employeeName.localeCompare(b.employeeName) || a.startTime - b.startTime);
        }
        sortableItems.sort((a, b) => {
            const getSortValue = (item: EditableTimeSegment, sortKey: SortableKeys): string | number => {
                switch(sortKey) {
                    case 'employeeName': return item.employeeName;
                    case 'startDate': return item.startTime;
                    case 'startTime': return item.startTime;
                    case 'stopDate': return item.endTime ?? Number.MAX_SAFE_INTEGER;
                    case 'stopTime': return item.endTime ?? Number.MAX_SAFE_INTEGER;
                    case 'activity': return item.activity;
                    case 'hours': return item.hours;
                    default: return 0;
                }
            };
            const aValue = getSortValue(a, key);
            const bValue = getSortValue(b, key);
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
            }
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return direction === 'asc' ? aValue - bValue : bValue - aValue;
            }
            return 0;
        });
        return sortableItems;
    }, [segments, sortConfig]);

    const totalPaidHours = useMemo(() => {
        const activityCalculationById = new Map(activities.map(activity => [activity.id, activity.calculation]));
        const leaveCalculationById = new Map(leaveCategories.map(category => [category.id, category.calculation]));

        return segments.reduce((total, segment) => {
            if (segment.source === 'time') {
                return activityCalculationById.get(segment.activityId) === ActivityCalculation.UNPAID ? total : total + segment.hours;
            }
            return leaveCalculationById.get(segment.activityId) === LeaveCategoryCalculation.UNPAID ? total : total + segment.hours;
        }, 0);
    }, [segments, activities, leaveCategories]);

    const formattedTotalPaidTime = filters.timeDisplayFormat === TimeDisplayFormat.DECIMAL
        ? totalPaidHours.toFixed(2)
        : formatDecimalHoursToHHMM(totalPaidHours);

    const singleEmployee = useMemo(() => {
        if (hasAppliedFilter && filters.employeeIds.length === 1) {
            return employeesInAnyGroup.find(e => e.id === filters.employeeIds[0]);
        }
        return null;
    }, [hasAppliedFilter, filters.employeeIds, employeesInAnyGroup]);

    const isLeaveBalanceButtonDisabled = useMemo(() => {
        return !singleEmployee || !singleEmployee.leaveBalances || singleEmployee.leaveBalances.length === 0;
    }, [singleEmployee]);

    const handleSort = (key: SortableKeys) => {
        setSortConfig(prevConfig => ({
            key,
            direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
        }));
    };
    const renderSortableHeader = (label: string, sortKey: SortableKeys, className = '') => {
        const isSorting = sortConfig.key === sortKey;
        const directionIcon = isSorting ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '';
        return (
            <th className={`p-3 font-semibold text-gray-500 dark:text-gray-300 ${className}`}>
                <button className="flex items-center gap-1 hover:text-gray-900 dark:hover:text-white transition-colors" onClick={() => handleSort(sortKey)} aria-label={`Sort by ${label}`}>
                    {label}
                    {directionIcon && <span className="text-xs text-brand-secondary">{directionIcon}</span>}
                </button>
            </th>
        );
    };
    const handleRowClick = (event: React.MouseEvent, clickedId: string, clickedIndex: number) => {
        const { shiftKey, ctrlKey, metaKey } = event;
        const isCtrlPressed = ctrlKey || metaKey;
        if (shiftKey && lastClickedIndex !== null) {
            const start = Math.min(clickedIndex, lastClickedIndex);
            const end = Math.max(clickedIndex, lastClickedIndex);
            const rangeIds = sortedSegments.slice(start, end + 1).map(s => s.id);
            setSelectedIds(rangeIds);
        } else if (isCtrlPressed) {
            setSelectedIds(prev => prev.includes(clickedId) ? prev.filter(id => id !== clickedId) : [...prev, clickedId]);
            setLastClickedIndex(clickedIndex);
        } else {
            setSelectedIds([clickedId]);
            setLastClickedIndex(clickedIndex);
        }
    };
    const handleContextMenu = (event: React.MouseEvent, segment: EditableTimeSegment) => {
        event.preventDefault();
        if (!selectedIds.includes(segment.id)) {
            setSelectedIds([segment.id]);
            setLastClickedIndex(sortedSegments.findIndex(s => s.id === segment.id));
        }
        setContextMenu({
            x: event.clientX,
            y: event.clientY,
        });
    };
    const areAnySelectedLocked = useMemo(() => {
        return selectedIds.some(id => {
            const seg = sortedSegments.find(s => s.id === id);
            return seg ? isSegmentLocked(seg) : false;
        });
    }, [selectedIds, sortedSegments, isSegmentLocked]);
    const areAnySelectedOrangeHRMLeave = useMemo(() => {
        return selectedIds.some(id => {
            const seg = sortedSegments.find(s => s.id === id);
            return seg?.source === 'leave' && seg.leaveEntryId?.startsWith('ohrm_');
        });
    }, [selectedIds, sortedSegments]);
    const handleDeleteClick = () => {
        if (areAnySelectedLocked) {
            alert("Cannot delete entries from a closed period.");
            return;
        }
        if (areAnySelectedOrangeHRMLeave) {
            alert("Leave entries from OrangeHRM cannot be deleted here.");
            return;
        }
        if (selectedIds.length > 0) setIsDeleteConfirmOpen(true);
    };
    const handleConfirmDelete = async () => {
        const timeLogIdsToDelete = new Set<string>();
        const leaveEntriesToModify = new Map<string, Set<string>>();
        for (const segId of selectedIds) {
            const segment = sortedSegments.find(s => s.id === segId);
            if (!segment) continue;
            if (segment.source === 'time' && segment.timeLogId) {
                timeLogIdsToDelete.add(segment.timeLogId);
            } else if (segment.source === 'leave' && segment.leaveEntryId && !segment.leaveEntryId.startsWith('ohrm_')) {
                if (!leaveEntriesToModify.has(segment.leaveEntryId)) {
                    leaveEntriesToModify.set(segment.leaveEntryId, new Set());
                }
                leaveEntriesToModify.get(segment.leaveEntryId)!.add(segment.employeeId);
            }
        }
        if (timeLogIdsToDelete.size > 0) {
            await deleteTimeLogs(Array.from(timeLogIdsToDelete));
        }
        for (const [leaveId, employeeIdsToRemoveSet] of leaveEntriesToModify.entries()) {
            const employeeIdsToRemove = Array.from(employeeIdsToRemoveSet);
            const originalEntry = leaveEntries.find(le => le.id === leaveId);
            if (originalEntry) {
                const newEmployeeIds = originalEntry.employeeIds.filter(id => !employeeIdsToRemove.includes(id));
                await updateLeaveEntry(leaveId, { employeeIds: newEmployeeIds });
            }
        }
        if (timeLogIdsToDelete.size > 0) {
            const employeesToUpdate = new Set<string>();
            selectedIds.forEach(id => {
                const seg = sortedSegments.find(s => s.id === id);
                if (seg?.source === 'time') employeesToUpdate.add(seg.employeeId);
            });
            const remainingTimeLogs = timeLogs.filter(log => !timeLogIdsToDelete.has(log.id));
            for (const empId of employeesToUpdate) {
                const employee = employees.find(e => e.id === empId);
                if (!employee) continue;
                const lastLog = remainingTimeLogs
                    .filter(log => log.employeeId === employee.emp_number)
                    .sort((a, b) => b.startTime - a.startTime)[0];
                let newStatus = EmployeeStatus.CLOCKED_OUT;
                if (lastLog && !lastLog.endTime) {
                    const activity = activities.find(a => a.id === lastLog.activityId);
                    newStatus = activity?.name === 'Lunch' ? EmployeeStatus.ON_BREAK : EmployeeStatus.CLOCKED_IN;
                }
                if (employee.status !== newStatus) {
                    await updateEmployee({ ...employee, status: newStatus });
                }
            }
        }
        setSelectedIds([]);
        setIsDeleteConfirmOpen(false);
    };
    const isEditDisabled = useMemo(() => {
        if (selectedIds.length !== 1 || areAnySelectedLocked) return true;
        const segment = sortedSegments.find(s => s.id === selectedIds[0]);
        if (!segment) return true;
        if (segment.source === 'leave' && segment.leaveEntryId?.startsWith('ohrm_')) {
            return true;
        }
        return false;
    }, [selectedIds, areAnySelectedLocked, sortedSegments]);
    const handleEdit = () => {
        if (isEditDisabled) return;
        const segmentToEdit = sortedSegments.find(s => s.id === selectedIds[0]);
        if (!segmentToEdit) return;
        if (segmentToEdit.source === 'time') {
            setEditingSegment(segmentToEdit);
            setIsModalOpen(true);
        } else if (segmentToEdit.source === 'leave') {
            setEditingSegment(segmentToEdit);
            setIsLeaveModalOpen(true);
        }
    };
    const handleAdd = () => {
        setEditingSegment(null);
        setIsModalOpen(true);
    };
    const handleSave = async (data: {
        employeeId: number,
        activityId: string,
        startTime: number,
        endTime?: number | null,
        lunch?: { startTime: number, endTime: number }
    }) => {
        const { employeeId, activityId, startTime, endTime, lunch } = data;
        if (editingSegment) {
            if (!editingSegment.timeLogId) return;
            await updateTimeLog(editingSegment.timeLogId, { employeeId, activityId, startTime, endTime });
            if (lunch) {
                await insertLunchBreak(editingSegment.timeLogId, lunch.startTime, lunch.endTime);
            }
        } else {
            const newLog = await addTimeLog({ employeeId, activityId, startTime, endTime, source: TimeEntrySource.ADMIN });
            if (lunch) {
                await insertLunchBreak(newLog.id, lunch.startTime, lunch.endTime, newLog);
            }
        }
        setIsModalOpen(false);
        setSelectedIds([]);
    };
    const handleApplyFilters = (newFilters: FilterSettings) => {
        setFilters(newFilters);
        setIsFilterModalOpen(false);
        setSelectedIds([]);
        setSortConfig({ key: 'default', direction: 'asc' });
        setHasAppliedFilter(true);
    };
    const handleInsertLunchClick = () => {
        if (areAnySelectedLocked) {
            alert("Cannot modify entries from a closed period.");
            return;
        }
        if (selectedIds.length !== 1) return;
        const segment = sortedSegments.find(s => s.id === selectedIds[0]);
        const isWorkActivity = segment?.activity === 'In';
        if (segment && segment.source === 'time' && isWorkActivity) {
            setSegmentToSplit(segment);
            setIsInsertLunchModalOpen(true);
        } else {
            alert("Please select a single 'In' segment to insert a lunch into.");
        }
    };
    const handleSaveLunch = async (data: { begin: number; end: number }) => {
        if (segmentToSplit?.timeLogId) {
            await insertLunchBreak(segmentToSplit.timeLogId, data.begin, data.end);
        }
        setIsInsertLunchModalOpen(false);
        setSegmentToSplit(null);
        setSelectedIds([]);
    };
    const handleSaveAddLeave = async (data: Omit<LeaveEntry, 'id' | 'createdAt'>) => {
        await addLeaveEntry(data);
        setIsAddLeaveModalOpen(false);
    };
    const handleSaveEditLeave = async (data: { leaveCategoryId: string, hoursPerDay: number }) => {
        if (editingSegment && editingSegment.leaveEntryId) {
            await updateLeaveEntry(editingSegment.leaveEntryId, {
                leaveCategoryId: data.leaveCategoryId,
                hoursPerDay: data.hoursPerDay,
            });
        }
        setIsLeaveModalOpen(false);
        setEditingSegment(null);
    };
    const isInsertLunchDisabled = useMemo(() => {
        if (selectedIds.length !== 1 || areAnySelectedLocked) return true;
        const segment = sortedSegments.find(s => s.id === selectedIds[0]);
        return !segment || segment.source === 'leave' || segment.activity !== 'In';
    }, [selectedIds, sortedSegments, areAnySelectedLocked]);
    return (
        <div>
            <div className="space-y-8">
				<ToolbarSection>			
                    <ToolbarButton icon={<FilterIcon />} label="Select Entries" onClick={() => setIsFilterModalOpen(true)} />
                    <ToolbarButton icon={<RefreshCwIcon />} label="Refresh" onClick={fetchData} disabled={!hasAppliedFilter} />
                    <ToolbarButton icon={<EditIcon />} label="Edit Entry" onClick={handleEdit} disabled={isEditDisabled} />
                    <ToolbarButton icon={<PlusSquareIcon />} label="Add Entry" onClick={handleAdd}/>
                    <ToolbarButton icon={<CalendarPlusIcon />} label="Add Leave" onClick={() => setIsAddLeaveModalOpen(true)} />
                    <ToolbarButton icon={<CalendarDaysIcon />} label="Leave Balances" onClick={() => setIsLeaveBalancesModalOpen(true)} disabled={isLeaveBalanceButtonDisabled} />
                    <ToolbarButton icon={<BurgerIcon />} label="Insert Lunch" onClick={handleInsertLunchClick} disabled={isInsertLunchDisabled} />
					<ToolbarButton icon={<TrashIcon className="text-red-500" />} label="Delete" onClick={handleDeleteClick} disabled={selectedIds.length === 0 || areAnySelectedLocked || areAnySelectedOrangeHRMLeave} />
                </ToolbarSection>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-x-auto select-none">
                <table className="w-full text-left text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th className="p-3 w-8"></th>
                            {renderSortableHeader('Name', 'employeeName')}
                            {renderSortableHeader('Start Date', 'startDate')}
                            {renderSortableHeader('Start Time', 'startTime')}
                            {renderSortableHeader('Stop Date', 'stopDate', 'hidden sm:table-cell')}
                            {renderSortableHeader('Stop Time', 'stopTime')}
                            {renderSortableHeader('Classification', 'activity', 'hidden sm:table-cell')}
                            {renderSortableHeader('Hours', 'hours', 'text-right hidden sm:table-cell')}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {sortedSegments.map((seg, index) => {
                            const isLeaveEntry = seg.source === 'leave';
                            const isOrangeHRMLeave = isLeaveEntry && seg.leaveEntryId?.startsWith('ohrm_');
                            const locked = isSegmentLocked(seg);
                            const isAutoClockOut = isAutoClockOutAtShiftStop(seg);
                            return (
                                <tr 
                                    key={seg.id} 
                                    onClick={(e) => handleRowClick(e, seg.id, index)}
                                    onDoubleClick={handleEdit}
                                    onContextMenu={(e) => handleContextMenu(e, seg)}
                                    className={`transition-colors 
										${locked || isOrangeHRMLeave 
											? 'cursor-not-allowed opacity-60'
											: 'cursor-pointer'}
										${selectedIds.includes(seg.id)
											? 'bg-blue-100 dark:bg-blue-900/50'
											: 'hover:bg-gray-100 dark:hover:bg-gray-700/50'}
										${isLeaveEntry 
											? 'text-sky-500 dark:text-sky-400'
											: 'text-gray-900 dark:text-gray-200'}
									`}
                                >
                                    <td className="p-3 text-center">
                                        {locked && <span title="This entry is in a closed period."><LockIcon className="w-4 h-4 text-gray-400" /></span>}
                                    </td>
                                    <td className="p-3">{seg.employeeName}</td>
                                    <td className="p-3">{seg.startDate}</td>
                                    <td className="p-3 font-mono">{seg.ui_startTime}</td>
                                    <td className="p-3 hidden sm:table-cell">{seg.stopDate}</td>
                                    <td className="p-3 font-mono">
                                        <span
                                            title={isAutoClockOut ? 'Auto Clock Out at default shift stop time' : undefined}
                                            className={isAutoClockOut ? 'inline-block rounded bg-amber-200 px-2 py-0.5 font-bold text-amber-950 dark:bg-amber-500/30 dark:text-amber-100' : undefined}
                                        >
                                            {seg.ui_stopTime}
                                        </span>
                                    </td>
                                    <td className="p-3 font-semibold hidden sm:table-cell">{seg.activity}</td>
                                    <td className="p-3 font-mono text-right hidden sm:table-cell">
                                        {filters.timeDisplayFormat === TimeDisplayFormat.DECIMAL
                                            ? seg.hours.toFixed(2)
                                            : formatDecimalHoursToHHMM(seg.hours)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {sortedSegments.length === 0 && (
                    <p className="text-center p-8 text-gray-500 dark:text-gray-400">
                        {hasAppliedFilter
                            ? "No time entries match the current filters."
                            : "Use the 'Select Entries' button to load timecard data."}
                    </p>
                )}
                {hasAppliedFilter && (
                    <div className="flex justify-end border-t border-gray-200 bg-gray-50 p-4 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white">
                        <div className="flex items-baseline gap-4">
                            <span className="text-sm font-semibold text-gray-600 dark:text-gray-300">Total Paid Time</span>
                            <span className="font-mono text-lg font-bold">{formattedTotalPaidTime}</span>
                        </div>
                    </div>
                )}
            </div>
            <EntryEditorModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleSave}
                employees={employeesForEntryModal}
                initialData={editingSegment}
                activities={activities}
                isLocked={!!editingSegment && isSegmentLocked(editingSegment)}
                lockTimestamp={lockTimestamp}
            />
             <LeaveEditorModal
                isOpen={isLeaveModalOpen}
                onClose={() => setIsLeaveModalOpen(false)}
                onSave={handleSaveEditLeave}
                segment={editingSegment}
                leaveCategories={leaveCategories}
                getGroupForEmployee={getGroupForEmployee}
                shifts={shifts}
                isLocked={!!editingSegment && isSegmentLocked(editingSegment)}
            />
            <SelectEntriesModal
                isOpen={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                onApply={handleApplyFilters}
                employees={employeesInAnyGroup}
                currentFilters={filters}
            />
             <AddLeaveModal
                isOpen={isAddLeaveModalOpen}
                onClose={() => setIsAddLeaveModalOpen(false)}
                onSave={handleSaveAddLeave}
                employees={activeEmployeesInAnyGroup}
                leaveCategories={leaveCategories}
                getGroupForEmployee={getGroupForEmployee}
                shifts={shifts}
                lockTimestamp={lockTimestamp}
            />
            <InsertLunchModal
                isOpen={isInsertLunchModalOpen}
                onClose={() => setIsInsertLunchModalOpen(false)}
                onSave={handleSaveLunch}
                segment={segmentToSplit}
            />
            <DeleteConfirmModal
                isOpen={isDeleteConfirmOpen}
                onClose={() => setIsDeleteConfirmOpen(false)}
                onConfirm={handleConfirmDelete}
                itemCount={selectedIds.length}
            />
            <LeaveBalancesModal
                isOpen={isLeaveBalancesModalOpen}
                onClose={() => setIsLeaveBalancesModalOpen(false)}
                employee={singleEmployee}
            />
            {contextMenu && (
                <ContextMenu x={contextMenu.x} y={contextMenu.y} onClose={() => setContextMenu(null)}>
                    <ContextMenuItem 
                        onClick={() => { handleEdit(); setContextMenu(null); }} 
                        disabled={isEditDisabled}
                    >
                        Edit
                    </ContextMenuItem>
                    <ContextMenuItem 
                        onClick={() => { handleInsertLunchClick(); setContextMenu(null); }} 
                        disabled={isInsertLunchDisabled}
                    >
                        Insert Lunch
                    </ContextMenuItem>
                    <ContextMenuItem 
                        onClick={() => { handleDeleteClick(); setContextMenu(null); }} 
                        disabled={selectedIds.length === 0 || areAnySelectedLocked || areAnySelectedOrangeHRMLeave}
                    >
                        Delete
                    </ContextMenuItem>
                </ContextMenu>
            )}
        </div>
    );
};

export default EntryEditor;
